import PropTypes from "prop-types";
import { memo } from "react";

// Common Sections
import CallActivityPerformanceComponent from "@PayPlans/components/Commons/CallActivityPerformance";
import LongevityComponent from "@PayPlans/components/Commons/Longevity";
import CustomerSatisfactionComponent from "@PayPlans/components/Commons/CustomerSatisfaction";
import TopPerformerBonus from "@PayPlans/components/Commons/TopPerformer";
import VariableOperationsGpsBonusComponent from "@PayPlans/components/Commons/VariableOperationsGpsBonus";
import VolumeBonusesComponent from "@/PayPlans/components/Commons/VolumeBonuses";

// Specific Sections
import AutoAlertsComponent from "@PayPlans/components/AutoAlerts";
import BonusAllowanceComponent from "@/PayPlans/components/Defaults/Sales/BonusAllowance";
import DefaultCommissionsComponent from "@/PayPlans/components/Defaults/Sales/DefaultCommissions";
import MiniDealComponent from "@/PayPlans/components/Defaults/Sales/MiniDeal";
import ModelBasedCommissionsComponent from "@/PayPlans/components/Defaults/Sales/ModelBasedCommissions";
import PacksComponent from "@/PayPlans/components/Defaults/Sales/Packs";

// Constants
const NEW_VEHICLES_LABEL = "New Vehicles";
const PRE_OWNED_VEHICLES_LABEL = "Pre-Owned Vehicles";

function AutoAlertsSection(props) {
	const { sectionId = "autoAlerts", content = null } = props;
	return <AutoAlertsComponent {...props} sectionId={sectionId} content={content} />;
}
AutoAlertsSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		new: PropTypes.object,
		preOwned: PropTypes.object,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function BonusAdjustmentsSection(props) {
	const {
		sectionId = "bonusAdjustments",
		upperColumns = {
			_id: "",
			actions: [],
			left: null,
			right: null,
		},
		lowerColumns = {
			_id: "",
			actions: [],
			left: null,
			right: null,
		},
		content = {
			commissions: [],
			adjustments: [],
			forgiveness: {
				input: "Unit",
				value: 0,
			},
			applyTiersRetroactively: false,
			subjectToReputation: false,
		},
		useForgivenessInput = false,
	} = props;
	return (
		<BonusAllowanceComponent
			{...props}
			sectionId={sectionId}
			upperColumns={upperColumns}
			lowerColumns={lowerColumns}
			content={content}
			useForgivenessInput={useForgivenessInput}
		/>
	);
}
BonusAdjustmentsSection.propTypes = {
	sectionId: PropTypes.string,
	upperColumns: PropTypes.object,
	lowerColumns: PropTypes.object,
	content: PropTypes.object,
	useForgivenessInput: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function CallActivityPerformanceSection(props) {
	const { sectionId = "callActivityPerformance", performance = null, paySwitchIsEditable = false } = props;
	return (
		<CallActivityPerformanceComponent
			{...props}
			sectionId={sectionId}
			performance={performance}
			paySwitchIsEditable={paySwitchIsEditable}
		/>
	);
}
CallActivityPerformanceSection.propTypes = {
	sectionId: PropTypes.string,
	performance: PropTypes.object,
	paySwitchIsEditable: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function CustomerSatisfactionSection(props) {
	const {
		sectionId = "customerSatisfaction",
		content = {
			makeMetrics: [],
			weighting: {
				weightingType: "UNITS",
				customWeights: [],
			},
		},
		excludesMakes = ["Cadillac"],
		errors = [],
		isService = false,
		useOnlyIndividualMetrics = false,
		useOnlyStoreMetrics = false,
	} = props;
	return (
		<CustomerSatisfactionComponent
			{...props}
			sectionId={sectionId}
			content={content}
			excludesMakes={excludesMakes}
			errors={errors}
			isService={isService}
			useOnlyIndividualMetrics={useOnlyIndividualMetrics}
			useOnlyStoreMetrics={useOnlyStoreMetrics}
		/>
	);
}
CustomerSatisfactionSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makeMetrics: PropTypes.array,
		weighting: PropTypes.shape({
			weightingType: PropTypes.string,
			customWeights: PropTypes.array,
		}),
	}),
	excludesMakes: PropTypes.array,
	errors: PropTypes.array,
	isService: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useOnlyIndividualMetrics: PropTypes.bool,
	useOnlyStoreMetrics: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function DefaultCommissionsSection(props) {
	const {
		sectionId = "default",
		content = null,
		formatVersion = null,
		errors = [],
		applyTiersRetroactively = false,
		globalUnitCounts = false,
		newAndPreOwnedRequired = false,
		isGuaranteedDraw = false,
		loading = false,
		loadingProducts = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	return (
		<DefaultCommissionsComponent
			sectionId={sectionId}
			commissions={content}
			frontProducts={formatVersion?.frontProducts || []}
			backProducts={formatVersion?.backProducts || []}
			tabsProps={{
				tabs: [NEW_VEHICLES_LABEL, PRE_OWNED_VEHICLES_LABEL],
				errors,
				applyTiersRetroactively,
				globalUnitCounts,
				newAndPreOwnedRequired,
				isGuaranteedDraw,
			}}
			loading={loading}
			loadingProducts={loadingProducts}
			readOnly={readOnly}
			onActionUpdate={onActionUpdate}
		/>
	);
}
DefaultCommissionsSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	formatVersion: PropTypes.object,
	errors: PropTypes.array,
	applyTiersRetroactively: PropTypes.bool,
	globalUnitCounts: PropTypes.bool,
	newAndPreOwnedRequired: PropTypes.bool,
	isGuaranteedDraw: PropTypes.bool,
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function LongevitySection(props) {
	const { sectionId = "longevity", content = [] } = props;
	return <LongevityComponent {...props} sectionId={sectionId} content={content} />;
}
LongevitySection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function MiniDealsSection(props) {
	const {
		sectionId = "miniDeal",
		brands = {
			new: [],
			preOwned: [],
		},
		errors = [],
		miniDeal = null,
		loading = false,
		loadingMakes = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	return (
		<MiniDealComponent
			sectionId={sectionId}
			brands={brands}
			tabsProps={{
				tabs: [NEW_VEHICLES_LABEL, PRE_OWNED_VEHICLES_LABEL],
				errors,
			}}
			miniDeal={miniDeal}
			loading={loading}
			loadingMakes={loadingMakes}
			readOnly={readOnly}
			onActionUpdate={onActionUpdate}
		/>
	);
}
MiniDealsSection.propTypes = {
	sectionId: PropTypes.string,
	miniDeal: PropTypes.object,
	brands: PropTypes.shape({
		new: PropTypes.array,
		preOwned: PropTypes.array,
	}),
	errors: PropTypes.array,
	loading: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function ModelCommissionsSection(props) {
	const {
		sectionId = "modelCommissions",
		content = null,
		formatVersion = null,
		makes = [],
		errors = [],
		frontProductLabel = "Total Gross Profit",
		backProductLabel = "Total Gross Profit",
		emptyError = {
			error: false,
			emptyMessage: "Please add one or more model-based commissions.",
		},
		applyTiersRetroactively = false,
		globalUnitCounts = false,
		loading = false,
		loadingProducts = false,
		loadingMakes = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	return (
		<ModelBasedCommissionsComponent
			sectionId={sectionId}
			commissions={content}
			frontProducts={formatVersion?.frontProducts || []}
			backProducts={formatVersion?.backProducts || []}
			makes={makes}
			tabsProps={{
				tabs: [NEW_VEHICLES_LABEL, PRE_OWNED_VEHICLES_LABEL],
				errors: errors || [],
				globalUnitCounts,
				applyTiersRetroactively,
			}}
			frontProductLabel={frontProductLabel}
			backProductLabel={backProductLabel}
			emptyError={emptyError}
			loading={loading}
			loadingProducts={loadingProducts}
			loadingMakes={loadingMakes}
			readOnly={readOnly}
			onActionUpdate={onActionUpdate}
		/>
	);
}
ModelCommissionsSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	formatVersion: PropTypes.object,
	makes: PropTypes.array,
	errors: PropTypes.array,
	frontProductLabel: PropTypes.string,
	backProductLabel: PropTypes.string,
	emptyError: PropTypes.object,
	applyTiersRetroactively: PropTypes.bool,
	globalUnitCounts: PropTypes.bool,
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function PacksSection(props) {
	const {
		sectionId = "packs",
		packs = null,
		errors = [],
		emptyError = {
			error: false,
			emptyMessage: "Please add one or more packs.",
		},
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	return (
		<PacksComponent
			sectionId={sectionId}
			packs={packs}
			tabsProps={{
				tabs: [NEW_VEHICLES_LABEL, PRE_OWNED_VEHICLES_LABEL],
				errors: errors || [],
			}}
			emptyError={emptyError}
			loading={loading}
			readOnly={readOnly}
			onActionUpdate={onActionUpdate}
		/>
	);
}
PacksSection.propTypes = {
	sectionId: PropTypes.string,
	packs: PropTypes.object,
	errors: PropTypes.array,
	emptyError: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function TopPerformanceBonusSection(props) {
	const { sectionId = "topSalesBonus", content = [] } = props;
	return <TopPerformerBonus {...props} sectionId={sectionId} content={content} />;
}
TopPerformanceBonusSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function VolumeBonusSection(props) {
	const {
		sectionId = "volumeBonus",
		position = "",
		content = {
			bonuses: [],
			preOwned: null,
			newAndPreOwnedRequired: false,
			combineFranchises: false,
			globalUnitCounts: false,
		},
		franchises = [],
	} = props;
	return <VolumeBonusesComponent {...props} sectionId={sectionId} position={position} content={content} franchises={franchises} />;
}
VolumeBonusSection.propTypes = {
	sectionId: PropTypes.string,
	position: PropTypes.string,
	content: PropTypes.object,
	franchises: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function VariableOperationsGpsBonusSection(props) {
	const {
		sectionId = "variableOperationsGpsBonus",
		position = "",
		content = {
			newVehicles: null,
			preOwned: null,
			globalUnitCounts: false,
		},
	} = props;
	return <VariableOperationsGpsBonusComponent {...props} sectionId={sectionId} position={position} content={content} />;
}
VariableOperationsGpsBonusSection.propTypes = {
	sectionId: PropTypes.string,
	position: PropTypes.string,
	content: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

const AutoAlerts = memo(AutoAlertsSection);
const BonusAdjustments = memo(BonusAdjustmentsSection);
const CallActivityPerformance = memo(CallActivityPerformanceSection);
const CustomerSatisfaction = memo(CustomerSatisfactionSection);
const DefaultCommissions = memo(DefaultCommissionsSection);
const Longevity = memo(LongevitySection);
const Packs = memo(PacksSection);
const MiniDeals = memo(MiniDealsSection);
const ModelCommissions = memo(ModelCommissionsSection);
const TopSalesBonus = memo(TopPerformanceBonusSection);
const VariableOperationsGpsBonus = memo(VariableOperationsGpsBonusSection);
const VolumeBonus = memo(VolumeBonusSection);

export {
	AutoAlerts,
	BonusAdjustments,
	CallActivityPerformance,
	CustomerSatisfaction,
	DefaultCommissions,
	Longevity,
	MiniDeals,
	ModelCommissions,
	Packs,
	TopSalesBonus,
	VariableOperationsGpsBonus,
	VolumeBonus,
};
